import { Button, Flex, IconButton, Input, InputGroup, InputLeftElement, TableContainer, Tooltip, useToast } from "@chakra-ui/react";
import { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { SectionHeader } from "../../../components";
import { LuSearch } from "react-icons/lu";
import { IconEdit, IconFileSpreadsheet, IconFileX, IconPlus } from "@tabler/icons-react";
import { DataTable } from "am-tax-fe-core";
import { BenchmarkReference } from "../../../api/benchmark/benchmarkApi";
import { TableRowProps } from "../../../utils/types";
import { useGetBenchmarkReferences } from "../../../api/benchmark/benchmarkQueries";
import { createColumnHelper } from "@tanstack/react-table";
import { BenchmarkReferenceModal } from "./BenchmarkReferenceModal";
import { downloadBenchmarkReferenceAsync } from "../../../api/benchmark/benchmarkApi";

export const BenchmarkReferencePanel: FunctionComponent = () => {
    const { data: benchmarkReferences = [], isLoading } = useGetBenchmarkReferences();
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [benchmarkReferenceData, setBenchmarkReferenceData] = useState<BenchmarkReference | undefined>(undefined);
    const [showCreateOrEditBenchmarkReferenceDialog, setShowCreateOrEditBenchmarkReferenceDialog] = useState<boolean>(false);
    const columnHelper = createColumnHelper<BenchmarkReference>();
    const toast = useToast();

    const filteredData = useMemo(() => {
        if (searchTerm.trim() === "") {
            return benchmarkReferences;
        } else {
            const value = searchTerm.trim().toLowerCase();
            return benchmarkReferences.filter(
                (b: BenchmarkReference) =>
                    b.blobStorageLink?.toLowerCase().includes(value) || b.region?.toLowerCase().includes(value) || b.serviceType?.toLowerCase().includes(value),
            );
        }
    }, [benchmarkReferences, searchTerm]);

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setSearchTerm(value);
    }

    const enableEditBenchmarkReferenceModal = (benchmarkReference: BenchmarkReference) => {
        setBenchmarkReferenceData(benchmarkReference);
        setShowCreateOrEditBenchmarkReferenceDialog(true);
    };

    const closeEditBenchmarkReferenceModal = () => {
        setShowCreateOrEditBenchmarkReferenceDialog(false);
        setBenchmarkReferenceData(undefined);
    };

    async function downloadBenchmarkReference(benchmarkReference: BenchmarkReference) {
        try {
            if (!benchmarkReference.blobStorageLink || benchmarkReference.blobStorageLink === "/") {
                toast({
                    title: "No File",
                    description: "No benchmark reference file is available for this benchmark reference",
                    status: "warning",
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const response = await downloadBenchmarkReferenceAsync(benchmarkReference.id || "");

            // Get the filename from the response headers or use a default
            const contentDisposition = response.headers.get("content-disposition");
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename =
                filenameMatch?.[1] ||
                benchmarkReference.blobStorageLink?.split("/").pop() ||
                `benchmark_reference_${benchmarkReference.region}_${benchmarkReference.serviceType}.xlsx`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast({
                title: "Download Complete",
                description: `Benchmark reference for ${benchmarkReference.region} - ${benchmarkReference.serviceType} has been downloaded`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Error downloading benchmark reference:", error);
            toast({
                title: "Download Failed",
                description: error instanceof Error ? error.message : "Failed to download benchmark reference",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    }

    return (
        <Flex flexFlow="column nowrap">
            <SectionHeader title="Benchmark References">
                <InputGroup flex="0 1 35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input placeholder="Search Benchmark References" onChange={handleFilter} />
                </InputGroup>
                <Button leftIcon={<IconPlus />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditBenchmarkReferenceDialog(true)}>
                    Add Benchmark Reference
                </Button>
            </SectionHeader>
            <TableContainer overflowY="auto" sx={{ mt: "1rem", backgroundColor: "chakra-body-bg", maxHeight: "70vh" }}>
                {" "}
                <DataTable
                    enableFilters={false}
                    tableProps={{
                        size: "sm",
                        sx: {
                            "tbody tr": {
                                cursor: "pointer",
                                transition: "all 0.2s ease-in-out",
                                _hover: {
                                    backgroundColor: "blue.50",
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                    _dark: {
                                        backgroundColor: "blue.900", // Adjust hover color for dark mode
                                    },
                                },
                            },
                        },
                    }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No references found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("region", {
                            header: "Region",
                            cell: (props: TableRowProps<BenchmarkReference>) => props.getValue(),
                        }),
                        columnHelper.accessor("serviceType", {
                            header: "Service Type",
                            cell: (props: TableRowProps<BenchmarkReference>) => props.getValue(),
                        }),
                        columnHelper.accessor("blobStorageLink", {
                            header: "Reference File",
                            cell: props => {
                                const hasFile = props.getValue() && props.getValue() !== "/";
                                return (
                                    <span>
                                        {hasFile ? (
                                            <>
                                                <IconFileSpreadsheet size={16} color="green" style={{ display: "inline", marginRight: "8px" }} />
                                                <button
                                                    onClick={() => downloadBenchmarkReference(props.row.original)}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    {props.getValue().split("/").pop()}
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <IconFileX size={16} color="gray" style={{ display: "inline", marginRight: "8px" }} />
                                                No file
                                            </>
                                        )}
                                    </span>
                                );
                            },
                        }),
                        {
                            id: "Edit Benchmark Reference",
                            cell: (props: TableRowProps<BenchmarkReference>) => {
                                const benchmarkReference = props.row.original;
                                return (
                                    <Tooltip label="Edit Benchmark Reference">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit Benchmark Reference"
                                            variant="outline"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEditBenchmarkReferenceModal(benchmarkReference);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <BenchmarkReferenceModal
                show={showCreateOrEditBenchmarkReferenceDialog}
                onClose={closeEditBenchmarkReferenceModal}
                rowData={benchmarkReferenceData}
            />
        </Flex>
    );
};
